import { Popover } from "@mui/material";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import { FramerTooltip } from "@ryde/controls";
import { useEmitterListener } from "@ryde/redux";
import {
  currencySignSelector,
  formatPrice,
  GlobalAppEventEnum,
  IRideDto,
  Log,
  TOOLTIP_DELAY_BUTTONS,
  useDrawers,
} from "@ryde/shared";

import { ButtonHeader } from "../../../../right";
import { RideHeaderPriceDropdown } from "./ride-header-price-dropdown";

interface IRideHeaderPrice {
  rideDto?: Partial<IRideDto>;
}

export const RideHeaderPrice: FC<IRideHeaderPrice> = ({ rideDto }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { openEditFormDrawer } = useDrawers();
  const currencySign = useRecoilValue(currencySignSelector);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = useEvent((): void => {
    setAnchorEl(null);
  });

  const handlePriceRateDrawerOpen = useEvent(
    async (id: number): Promise<void> => {
      setAnchorEl(null);
      openEditFormDrawer({
        entity: "priceRate",
        id,
      });
      Log.debug(`Form form for Price Rate with id ${id} closed`);
    },
  );
  const open = !!anchorEl;
  const priceText = `${currencySign}${formatPrice({ value: rideDto?.price ?? 0 })}`;

  useEmitterListener({
    event: GlobalAppEventEnum.START_DRAG,
    handler: handleClose,
    isEnabled: open,
  });

  return (
    rideDto && (
      <>
        {/* The button shows the price alone (RYDE-8064); what it opens is carried by the tooltip and the
            accessible name, so the header stays readable without losing the affordance. */}
        <FramerTooltip
          content={t("general.BREAKDOWN")}
          mouseEnterDelay={TOOLTIP_DELAY_BUTTONS}
        >
          <ButtonHeader
            onClick={handleClick}
            $active={open}
            aria-label={`${t("general.BREAKDOWN")} ${priceText}`}
          >
            <SVG_MUI.Coins2SVG data-e2e="ride-svg-price" />
            <span data-e2e="ride-price">{priceText}</span>
          </ButtonHeader>
        </FramerTooltip>
        {open && (
          <Popover
            id={"ride-price-popover"}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 40,
              horizontal: "left",
            }}
          >
            <RideHeaderPriceDropdown
              handlePriceRateDrawerOpen={handlePriceRateDrawerOpen}
              onClosePopover={handleClose}
              rideDto={rideDto}
            />
          </Popover>
        )}
      </>
    )
  );
};
